import {
  Suspense,
  forwardRef,
  lazy,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  ChevronLeft,
  ChevronRight,
  CrossIcon,
  RefreshIcon,
} from 'assets/icons';
import Button from 'components/button';
import { fillText } from 'components/invoiceImport/invoiceImportText';
import { downloadFile } from 'components/invoiceList/useInvoiceExport';
import Tooltip from 'components/tooltips/Tooltips';
import InvoicePdfFullscreen, { TOOLTIP_Z_INDEX } from './InvoicePdfFullscreen';

const InvoicePdfDocument = lazy(() => import('./InvoicePdfDocument'));

// Relative to the page fitted to the width of its area: 1 is that fit.
const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const FIT_ZOOM_STEP = 2;

// The area's padding on each side (p-6), off the width the page can take.
const PAGE_PADDING = 24;
const MIN_PAGE_WIDTH = 200;
// Until the area is measured (or where it cannot be, as in jsdom).
const FALLBACK_PAGE_WIDTH = 560;

const TOOL_CLASS_NAME =
  'w-8 h-8 flex items-center justify-center rounded-md text-gray-700 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[transparent]';

// An icon-only control: its label is both its accessible name and its tooltip.
const ToolButton = forwardRef(
  ({ label, tooltipZIndex, children, ...props }, ref) => (
    <Tooltip text={label} zIndex={tooltipZIndex}>
      <button
        ref={ref}
        type="button"
        className={TOOL_CLASS_NAME}
        aria-label={label}
        {...props}
      >
        {children}
      </button>
    </Tooltip>
  ),
);

const MagnifierIcon = ({ withPlus }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="11" cy="11" r="7" stroke="#374151" strokeWidth="2" />
    <path
      d="M21 21l-4.35-4.35M8 11h6"
      stroke="#374151"
      strokeWidth="2"
      strokeLinecap="round"
    />
    {withPlus && (
      <path
        d="M11 8v6"
        stroke="#374151"
        strokeWidth="2"
        strokeLinecap="round"
      />
    )}
  </svg>
);

const FitWidthIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M4 5v14M20 5v14M8 12h8M8 12l2.5-2.5M8 12l2.5 2.5M16 12l-2.5-2.5M16 12l-2.5 2.5"
      stroke="#374151"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ExpandIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"
      stroke="#374151"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const DownloadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 4v11m0 0l-4-4m4 4l4-4M5 19h14"
      stroke="#374151"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// The message of each failure, and whether trying again can help.
const FAILURES = {
  busy: {
    key: 'busy',
    fallback: 'Un import est en cours, réessayez dans un instant.',
    canRetry: true,
  },
  notFound: {
    key: 'notFound',
    fallback: 'Le PDF de cette facture est introuvable.',
    canRetry: false,
  },
  besttFailed: {
    key: 'besttFailed',
    fallback: 'Bestt n’a pas rendu le PDF, réessayez dans un instant.',
    canRetry: true,
  },
  failed: {
    key: 'failed',
    fallback: 'L’aperçu n’a pas pu être chargé.',
    canRetry: true,
  },
};

const Centered = ({ children, role }) => (
  <div
    role={role}
    className="flex flex-col items-center justify-center gap-3 h-full py-16 px-6 text-center text-sm text-gray-600"
  >
    {children}
  </div>
);

// The width of an element's content, followed as it resizes.
const useContentWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const measure = () => setWidth(element.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
};

// What one viewer shows: zoom, rotation and page.
const usePdfViewerState = (initial = {}) => {
  const [zoomStep, setZoomStep] = useState(FIT_ZOOM_STEP);
  const [rotation, setRotation] = useState(initial.rotation ?? 0);
  const [pageCount, setPageCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(initial.pageNumber ?? 1);
  // The full-window viewer opens on the panel's page: kept if the document
  // has it.
  const onLoadSuccess = useCallback(({ numPages }) => {
    setPageCount(numPages);
    setPageNumber((page) => Math.min(Math.max(page, 1), numPages));
  }, []);
  return {
    zoomStep,
    setZoomStep,
    rotation,
    setRotation,
    pageCount,
    pageNumber,
    setPageNumber,
    onLoadSuccess,
  };
};

/**
 * A toolbar and the page under it, fitted to the width of its area. The
 * panel and the full-window viewer each have one, with its own state.
 * `placeholder` takes the area's place while there is no document to show.
 */
const PdfViewer = ({
  file,
  placeholder,
  viewer,
  previewText,
  onLoadError,
  tooltipZIndex,
  extraTools,
}) => {
  const [areaRef, areaWidth] = useContentWidth();
  const hasDocument = Boolean(file) && !placeholder;
  const {
    zoomStep,
    setZoomStep,
    rotation,
    setRotation,
    pageCount,
    pageNumber,
    setPageNumber,
    onLoadSuccess,
  } = viewer;
  const pageWidth =
    areaWidth > 0
      ? Math.max(MIN_PAGE_WIDTH, areaWidth - 2 * PAGE_PADDING)
      : FALLBACK_PAGE_WIDTH;
  const loading = (
    <Centered role="status">
      {previewText('loading', 'Chargement du PDF…')}
    </Centered>
  );

  return (
    <div className="flex flex-col flex-1 min-w-0 min-h-0 bg-gray-50">
      <div className="flex-shrink-0 flex items-center justify-end gap-1 px-4 py-2">
        {pageCount > 1 && (
          <div className="flex items-center gap-1 mr-auto text-sm text-gray-600">
            <ToolButton
              label={previewText('previousPage', 'Page précédente')}
              tooltipZIndex={tooltipZIndex}
              disabled={pageNumber <= 1}
              onClick={() => setPageNumber((page) => page - 1)}
            >
              <ChevronLeft wh={18} color="#374151" />
            </ToolButton>
            <span>
              {fillText(previewText('pageOf', '{page} / {pages}'), {
                page: pageNumber,
                pages: pageCount,
              })}
            </span>
            <ToolButton
              label={previewText('nextPage', 'Page suivante')}
              tooltipZIndex={tooltipZIndex}
              disabled={pageNumber >= pageCount}
              onClick={() => setPageNumber((page) => page + 1)}
            >
              <ChevronRight wh={18} color="#374151" />
            </ToolButton>
          </div>
        )}
        <ToolButton
          label={previewText('rotate', 'Pivoter')}
          tooltipZIndex={tooltipZIndex}
          disabled={!hasDocument}
          onClick={() => setRotation((angle) => (angle + 90) % 360)}
        >
          <RefreshIcon wh={18} color="#374151" />
        </ToolButton>
        <ToolButton
          label={previewText('zoomOut', 'Zoom arrière')}
          tooltipZIndex={tooltipZIndex}
          disabled={!hasDocument || zoomStep === 0}
          onClick={() => setZoomStep((step) => step - 1)}
        >
          <MagnifierIcon />
        </ToolButton>
        <ToolButton
          label={previewText('zoomIn', 'Zoom avant')}
          tooltipZIndex={tooltipZIndex}
          disabled={!hasDocument || zoomStep === ZOOM_STEPS.length - 1}
          onClick={() => setZoomStep((step) => step + 1)}
        >
          <MagnifierIcon withPlus />
        </ToolButton>
        <ToolButton
          label={previewText('fitWidth', 'Ajuster à la largeur')}
          tooltipZIndex={tooltipZIndex}
          disabled={!hasDocument || zoomStep === FIT_ZOOM_STEP}
          onClick={() => setZoomStep(FIT_ZOOM_STEP)}
        >
          <FitWidthIcon />
        </ToolButton>
        {extraTools}
      </div>
      {/* The gutter stays reserved: a scrollbar that came and went with the
          page's height would change the fit, and the height with it. */}
      <div
        ref={areaRef}
        className="flex-1 min-h-0 overflow-auto"
        style={{ scrollbarGutter: 'stable' }}
      >
        {placeholder ?? (
          <div className="flex justify-center p-6 min-w-fit">
            <Suspense fallback={loading}>
              <InvoicePdfDocument
                file={file}
                pageNumber={pageNumber}
                width={pageWidth}
                scale={ZOOM_STEPS[zoomStep]}
                rotation={rotation}
                onLoadSuccess={onLoadSuccess}
                onLoadError={onLoadError}
              />
            </Suspense>
          </div>
        )}
      </div>
    </div>
  );
};

// The full-window viewer's own state, opened on the panel's page and angle.
const FullscreenPdfViewer = ({ initial, ...props }) => {
  const viewer = usePdfViewerState(initial);
  return (
    <PdfViewer {...props} viewer={viewer} tooltipZIndex={TOOLTIP_Z_INDEX} />
  );
};

/**
 * The left half of the invoice detail (JBWEB-1274): its PDF, with zoom,
 * rotation and pages, and « Agrandir » to see it over the whole window
 * (JBWEB-1389), from the same blob. `pdf` comes from `useInvoicePdf`,
 * `number` names the downloaded file.
 */
const InvoicePdfPreview = ({ pdf, text, number }) => {
  const previewText = (key, fallback) => text(`preview.${key}`, fallback);
  const viewer = usePdfViewerState();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const expandButtonRef = useRef(null);

  const closeFullscreen = useCallback(() => {
    setIsFullscreen(false);
    expandButtonRef.current?.focus();
  }, []);

  let placeholder = null;
  if (pdf.failure) {
    const failure = FAILURES[pdf.failure] ?? FAILURES.failed;
    placeholder = (
      <Centered role="alert">
        <div>{previewText(failure.key, failure.fallback)}</div>
        {failure.canRetry && (
          <Button color="SECONDARY" onClick={pdf.reload}>
            {previewText('retry', 'Réessayer')}
          </Button>
        )}
      </Centered>
    );
  } else if (pdf.isLoading || !pdf.blob) {
    placeholder = (
      <Centered role="status">
        {previewText('loading', 'Chargement du PDF…')}
      </Centered>
    );
  }

  const hasDocument = !placeholder;
  const fullscreenTitle = fillText(
    previewText('fullscreenTitle', 'Facture n° {number}'),
    { number },
  );

  return (
    <>
      <PdfViewer
        file={pdf.blob}
        placeholder={placeholder}
        viewer={viewer}
        previewText={previewText}
        onLoadError={pdf.markUnreadable}
        extraTools={
          <ToolButton
            ref={expandButtonRef}
            label={previewText('expand', 'Agrandir')}
            disabled={!hasDocument}
            onClick={() => setIsFullscreen(true)}
          >
            <ExpandIcon />
          </ToolButton>
        }
      />
      {isFullscreen && hasDocument && (
        <InvoicePdfFullscreen
          title={fullscreenTitle}
          onClose={closeFullscreen}
          actions={
            <>
              <ToolButton
                label={previewText('download', 'Télécharger le PDF')}
                tooltipZIndex={TOOLTIP_Z_INDEX}
                onClick={() => downloadFile(pdf.blob, `facture-${number}.pdf`)}
              >
                <DownloadIcon />
              </ToolButton>
              <ToolButton
                label={previewText('closeFullscreen', 'Fermer l’aperçu')}
                tooltipZIndex={TOOLTIP_Z_INDEX}
                onClick={closeFullscreen}
              >
                <CrossIcon wh={20} color="#111827" />
              </ToolButton>
            </>
          }
        >
          <FullscreenPdfViewer
            file={pdf.blob}
            initial={{
              pageNumber: viewer.pageNumber,
              rotation: viewer.rotation,
            }}
            previewText={previewText}
            onLoadError={pdf.markUnreadable}
          />
        </InvoicePdfFullscreen>
      )}
    </>
  );
};

export default InvoicePdfPreview;
